import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { SkyDialog, SkyText } from "@skypointcloud/skypoint-ui";

interface ConfirmOptions {
  okLabel?: string;
  cancelLabel?: string;
  title?: string;
  subText?: string;
  confirmation?: React.ReactNode;
  showOk?: boolean;
  showCancel?: boolean;
  classAlert?: string;
  maxWidth?: number;
  minWidth?: number;
  style?: React.CSSProperties;
  dialogContentStyles?: React.CSSProperties;
  isBlocking?: boolean;
  isModeless?: boolean;
  item?: string;
  itemsCannotBeDeleted?: string[];
  isDisabled?: boolean;
  enableEscape?: boolean;
}

interface ConfirmState extends ConfirmOptions {
  message: React.ReactNode;
  resolve: () => void;
  reject: () => void;
  open: boolean;
}

interface ConfirmationContextValue {
  confirm: (message: React.ReactNode, options?: ConfirmOptions) => Promise<void>;
}

const ConfirmationContext = createContext<ConfirmationContextValue | null>(null);

export const useConfirm = (): ConfirmationContextValue["confirm"] => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error("useConfirm must be used within a ConfirmationProvider");
  }
  return context.confirm;
};

interface ConfirmationProviderProps {
  children: React.ReactNode;
}

export const ConfirmationProvider: React.FC<ConfirmationProviderProps> = ({ children }) => {
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);

  const confirm = useCallback(
    (message: React.ReactNode, options: ConfirmOptions = {}): Promise<void> => {
      return new Promise<void>((resolve, reject) => {
        setConfirmState({
          message,
          ...options,
          resolve,
          reject,
          open: true,
        });
      });
    },
    [],
  );

  // Register the confirm function globally for legacy confirm() usage
  useEffect(() => {
    // Dynamically import to avoid circular dependency
    import("./confirm").then((module) => {
      module.setConfirmFunction(confirm);
    });
  }, [confirm]);

  const handleClose = useCallback(() => {
    if (confirmState) {
      confirmState.reject();
      setConfirmState(null);
      document.body.style.setProperty("overflow", "auto");
    }
  }, [confirmState]);

  const handleConfirm = useCallback(() => {
    if (confirmState) {
      confirmState.resolve();
      setConfirmState(null);
      document.body.style.setProperty("overflow", "auto");
    }
  }, [confirmState]);

  const handleCancel = useCallback(() => {
    if (confirmState) {
      confirmState.reject();
      setConfirmState(null);
      document.body.style.setProperty("overflow", "auto");
    }
  }, [confirmState]);

  // Handle body overflow when dialog opens/closes
  useEffect(() => {
    if (confirmState?.open) {
      document.body.style.setProperty("overflow", "hidden");
    }
    return () => {
      document.body.style.setProperty("overflow", "auto");
    };
  }, [confirmState?.open]);

  if (!confirmState) {
    return (
      <ConfirmationContext.Provider value={{ confirm }}>{children}</ConfirmationContext.Provider>
    );
  }

  const {
    message,
    okLabel = "Yes",
    cancelLabel = "No",
    title = "Delete Confirm",
    subText,
    showOk = true,
    showCancel = true,
    classAlert = "",
    maxWidth,
    minWidth,
    style,
    dialogContentStyles,
    isBlocking,
    isModeless,
    item,
    itemsCannotBeDeleted,
    isDisabled,
    enableEscape = true,
  } = confirmState;

  return (
    <ConfirmationContext.Provider value={{ confirm }}>
      {children}
      <SkyDialog
        open={confirmState.open}
        onOpenChange={(open) => {
          if (!open && enableEscape) {
            handleClose();
          }
        }}
        title={title}
        variant={isBlocking !== false && !isModeless ? "modal" : undefined}
        primaryAction={
          showOk
            ? {
                label: okLabel,
                onClick: handleConfirm,
                disabled: isDisabled,
              }
            : undefined
        }
        secondaryAction={
          showCancel
            ? {
                label: cancelLabel,
                onClick: handleCancel,
              }
            : undefined
        }
      >
        <div className="flex flex-col gap-2">
          {subText && <SkyText>{subText}</SkyText>}
          {itemsCannotBeDeleted && itemsCannotBeDeleted.length ? (
            <>
              <SkyText className={classAlert}>Following {item} cannot be deleted</SkyText>
              <ul className="list-disc pl-5 text-sm">
                {itemsCannotBeDeleted.map((value) => (
                  <li key={value}>{value}</li>
                ))}
              </ul>
              {typeof message === "string" ? (
                <SkyText className={classAlert}>{message}</SkyText>
              ) : (
                <div className={classAlert}>{message}</div>
              )}
            </>
          ) : typeof message === "string" ? (
            <SkyText className={classAlert}>{message}</SkyText>
          ) : (
            <div className={classAlert}>{message}</div>
          )}
        </div>
      </SkyDialog>
    </ConfirmationContext.Provider>
  );
};
